import * as React from "react";
import { IFieldOptionValue } from "@montana/shared/form/field/field";
import { FieldWrapper } from "@montana/shared/form/field-wrapper";

import { Checkable, IRadioProps as IRadioBaseProps } from "../checkable";
import { withForm } from "../field";
import { isFieldTouched, isFieldValid } from "../field/field.component";

interface IRadioProps extends Omit<IRadioBaseProps, "type" | "radioGroupValue"> {
    options: IFieldOptionValue[];
}

/**
 * @author SBF & AGI & LMU & FST
 *
 * @description
 * Can be used outside forms
 *
 * @example
 * <Radio options={options} value={value} onCheck={(newValue): void => setValue(newValue)} />
 */
export const Radio: React.FC<IRadioProps> = (props: IRadioProps): JSX.Element => {

    const {
        options,
        label,
        value,
        id,
        isValid,
        errorMessage,
        isTouched,
        compact,
        ...fieldProps
    } = props;

    return (
        <FieldWrapper
            label={label || ""}
            id={id || ""}
            typeName="radio"
            isValid={isValid}
            isTouched={isTouched}
            errorMessage={errorMessage}
            compact={compact}
            dontRenderLabel
        >
            {options.map((option, idx): JSX.Element => (
                <Checkable
                    key={idx}
                    type="radio"
                    id={id ? `${id}--${idx}` : undefined}
                    radioGroupValue={value}
                    value={option.value as string}
                    compact={compact}
                    disabled={option.disabled}
                    {...fieldProps}
                >
                    {option.text}
                    {option.element}
                </Checkable>
            ))}
        </FieldWrapper>
    );
};


/**
 * @author SBF
 *
 * @description
 * Must be used in the context of a <Form>
 *
 * @example
 *  <Form>
 *      <FormRadio name="radio-grp" value="1" validate={requried("Must be checked")}>Radio 1</FormRadio>
 *      <FormRadio name="radio-grp" value="2" validate={requried("Must be checked")}>Radio 2</FormRadio>
 *  </Form>
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FormRadio = withForm<IRadioProps, 'value' | 'onCheck' | 'isValid' | "isTouched" | "errorMessage">(Radio, (field, form): any => ({
    radioGroupValue: field.value,
    onCheck: (value: unknown): void => {
        form.setFieldTouched(field.name, true);
        form.setFieldValue(field.name, value);
    },
    errorMessage: (form.errors as { [key: string]: string })[field.name],
    isValid: isFieldValid(field.name, form),
    isTouched: isFieldTouched(field.name, form),
}));
